// Story 1.28 — TanStack Query hook for
// GET /api/v1/admin/portfolio-applications/:id.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type AdminPortfolioApplicationDetail = {
  portfolio_application_id: string;
  portfolio_id: string;
  portfolio_title: string;
  portfolio_theme: string | null;
  portfolio_recruiting_status: string;
  user_id: string;
  creator_display_name: string | null;
  creator_hello_text: string | null;
  creator_handle: string | null;
  creator_image_blob_path: string | null;
  optional_drop_specific_hello_text: string | null;
  effective_hello_text: string | null;
  status: "pending" | "approved" | "rejected" | "withdrawn";
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  applicant_email: string;
  applicant_email_verified: boolean;
  applicant_account_created_at: string;
};

export type AdminPortfolioApplicationDetailResponse = {
  data: AdminPortfolioApplicationDetail;
};

async function fetchDetail(
  id: string,
): Promise<AdminPortfolioApplicationDetailResponse> {
  const response = await fetch(`/api/v1/admin/portfolio-applications/${id}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Detail fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminPortfolioApplicationDetailResponse;
}

export function useAdminPortfolioApplicationDetail(id: string) {
  return useQuery<AdminPortfolioApplicationDetailResponse, Error>({
    queryKey: ["admin-portfolio-application", id],
    queryFn: () => fetchDetail(id),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
